/**
 * Frontend client for quote2cart_ecwid BFF → quote2cart_api
 */
import { getStoreId } from './storage';
import * as logger from './logger';

const backendBase = () => {
  const raw = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '');
  // Same-origin (Vite proxy) when unset or pointing at local backend — browser
  // inside the Ecwid iframe cannot reach the host's 127.0.0.1.
  if (!raw || /127\.0\.0\.1|localhost/i.test(raw)) return '';
  return raw;
};

function buildUrl(path, query = {}) {
  const base = backendBase();
  const url = base ? new URL(`${base}${path}`) : new URL(path, window.location.origin);
  const storeId = getStoreId();
  if (storeId) url.searchParams.set('id', storeId);
  Object.entries(query).forEach(([k, v]) => {
    if (v != null && v !== '') url.searchParams.set(k, String(v));
  });
  return url.toString();
}

async function request(method, path, { query, body } = {}) {
  const opts = {
    method,
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  } else if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = '{}';
  }
  const res = await fetch(buildUrl(path, query), opts);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }
  if (!res.ok) {
    const message = data?.error?.message || data?.error || `HTTP ${res.status}`;
    logger.error('apiClient', method, path, message);
    const err = new Error(typeof message === 'string' ? message : 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

async function uploadFile(path, file, fieldName = 'file') {
  const form = new FormData();
  form.append(fieldName, file);
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }
  if (!res.ok) {
    const message = data?.error?.message || data?.error || `HTTP ${res.status}`;
    logger.error('apiClient', 'POST', path, message);
    const err = new Error(typeof message === 'string' ? message : 'Upload failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  health: () => request('GET', '/api/q2c/health'),
  dashboard: () => request('GET', '/api/q2c/quotes/dashboard'),
  listQuotes: (query) => request('GET', '/api/q2c/quotes', { query }),
  getQuote: (quoteId) => request('GET', `/api/q2c/quotes/${quoteId}`),
  createQuote: (body) => request('POST', '/api/q2c/quotes', { body }),
  updateQuote: (quoteId, body) => request('PATCH', `/api/q2c/quotes/${quoteId}`, { body }),
  saveDraft: (quoteId) => request('POST', `/api/q2c/quotes/${quoteId}/draft`),
  sendQuote: (quoteId) => request('POST', `/api/q2c/quotes/${quoteId}/send`),
  previewEmail: (quoteId, email) =>
    request('POST', `/api/q2c/quotes/${quoteId}/preview-email`, { body: { email } }),
  quoteHistory: (quoteId) => request('GET', `/api/q2c/quotes/${quoteId}/history`),
  completeQuote: (quoteId) => request('POST', `/api/q2c/quotes/${quoteId}/complete`),
  archiveQuote: (quoteId) => request('POST', `/api/q2c/quotes/${quoteId}/archive`),
  cancelQuote: (quoteId) => request('POST', `/api/q2c/quotes/${quoteId}/cancel`),
  getQuoteDocument: (quoteId, format) =>
    request('GET', `/api/q2c/quotes/${quoteId}/document`, {
      query: format ? { format } : {},
    }),
  getSettings: () => request('GET', '/api/q2c/settings'),
  getSettingsSection: (section) => request('GET', `/api/q2c/settings/${section}`),
  putSettingsSection: (section, body) =>
    request('PUT', `/api/q2c/settings/${section}`, { body }),
  buttonPickerPageUrl: (pageUrl, lang) => buildUrl('/api/q2c/button-picker', { url: pageUrl, lang }),
  uploadStoreLogo: (file) => uploadFile('/api/q2c/settings/logo', file),
  testEmail: (email) => request('POST', '/api/q2c/settings/email/test', { body: { email } }),
  getWidgetStatus: () => request('GET', '/api/q2c/settings/widget-status'),
  publishWidget: () => request('POST', '/api/q2c/settings/widget/publish'),
  disableWidget: () => request('POST', '/api/q2c/settings/widget/disable'),
  searchProducts: (q) => request('GET', '/api/q2c/products/search', { query: { q } }),
  productCategories: () => request('GET', '/api/q2c/products/categories'),
  productsByCategory: (categoryId, { limit, offset } = {}) =>
    request('GET', '/api/q2c/products/by-category', {
      query: { categoryId, limit, offset },
    }),
  productsBySkuPrefix: (skuPrefix, { limit, offset } = {}) =>
    request('GET', '/api/q2c/products/by-sku-prefix', {
      query: { skuPrefix, limit, offset },
    }),
  productsByIds: (ids) =>
    request('GET', '/api/q2c/products/by-ids', {
      query: { ids: ids.join(',') },
    }),
  getShippingRates: (body) => request('POST', '/api/q2c/shipping/rates', { body }),
  getDiscountSettings: () => request('GET', '/api/q2c/discounts/settings'),
  putDiscountSettings: (body) => request('PUT', '/api/q2c/discounts/settings', { body }),
  getCustomPrices: () => request('GET', '/api/q2c/discounts/custom-prices'),
  putCustomPrices: (customPrices) =>
    request('PUT', '/api/q2c/discounts/custom-prices', { body: { customPrices } }),
  getDiscountExclusions: () => request('GET', '/api/q2c/discounts/exclusions'),
  putDiscountExclusions: (exclusions) =>
    request('PUT', '/api/q2c/discounts/exclusions', { body: { exclusions } }),
  previewDiscounts: (products) =>
    request('POST', '/api/q2c/discounts/preview', {
      body: products ? { products } : {},
    }),
  searchCustomers: (q) => request('GET', '/api/q2c/customers/search', { query: { q } }),
  customerGroups: () => request('GET', '/api/q2c/customers/groups'),
  syncCustomers: () => request('POST', '/api/q2c/customers/sync'),
  listCompanies: (q) => request('GET', '/api/q2c/customers/companies', { query: { q } }),
  listCustomers: (q) => request('GET', '/api/q2c/customers', { query: { q } }),
  getCustomer: (customerId) => request('GET', `/api/q2c/customers/${customerId}`),
  patchCustomer: (customerId, body) =>
    request('PATCH', `/api/q2c/customers/${customerId}`, { body }),
  createCustomerGroup: (name) => request('POST', '/api/q2c/customers/groups', { body: { name } }),
  updateCustomerGroup: (groupId, name) =>
    request('PUT', `/api/q2c/customers/groups/${groupId}`, { body: { name } }),
  deleteCustomerGroup: (groupId) => request('DELETE', `/api/q2c/customers/groups/${groupId}`),
  listGroupMembers: () => request('GET', '/api/q2c/customers/groups/members'),
  addGroupMember: (groupId, email) =>
    request('POST', '/api/q2c/customers/groups/members', { body: { groupId, email } }),
  removeGroupMember: (groupId, email) =>
    request('DELETE', '/api/q2c/customers/groups/members', { body: { groupId, email } }),
  widgetConfig: () => request('GET', '/api/q2c/widget-config'),
  listChatThreads: () => request('GET', '/api/q2c/chat/threads'),
  createChatThread: (quoteId) =>
    request('POST', '/api/q2c/chat/threads', { body: { quoteId } }),
  listChatMessages: (threadId) =>
    request('GET', `/api/q2c/chat/threads/${threadId}/messages`),
  postChatMessage: (threadId, body) =>
    request('POST', `/api/q2c/chat/threads/${threadId}/messages`, { body: { body } }),
  getChatWsTicket: () => request('POST', '/api/q2c/chat/ws-ticket'),
  register: () =>
    request('POST', '/api/register', {
      query: { id: getStoreId() },
    }),
};

export default api;
